import React from 'react'
import { TariffUpgradeType } from '../../methods/TariffUpgradeType'
import { formatPromoPackageBonus } from '../../util/promoCode'
import { applyDiscountToPrice } from '../../util/priceCalculation'
import { firstInstallmentKopecks, isInstallmentPackage } from '../../util/installment'
import { isAdditionalService } from '../../util/AdditionalServiceType'
import trashIcon from '../../images/pages/tariffsV2/trash.svg'
import 'pages/Styles/OrderInfo.sass'
import 'pages/Styles/UIKit_v2.sass'

type Props = {
    showTitle: boolean
    cart: any[]
    promoCodes?: any
    onRemoveItem?: (itemId: string, itemType: string) => void
    onRemovePackageUpgrade?: (upgradeId: string) => void
}

function OrderInfo({ showTitle, cart, promoCodes, onRemoveItem, onRemovePackageUpgrade }: Props) {
    function getUpgradeName(upgradeId: string): string {
        return TariffUpgradeType.getTitle(upgradeId as TariffUpgradeType)
    }

    function itemKopecks(item: any): number {
        if (item.type === 'package_v2') {
            const discount = item.discount || 0
            let packagePrice = applyDiscountToPrice(item.price, discount)
            if (item.tariff_upgrades && item.tariff_upgrades.length > 0) {
                item.tariff_upgrades.forEach((upgradeId: string) => {
                    if (item.upgrade_additional_price && item.upgrade_additional_price[upgradeId]) {
                        packagePrice += item.upgrade_additional_price[upgradeId]
                    }
                })
            }
            if (isInstallmentPackage(item)) {
                return firstInstallmentKopecks(packagePrice)
            }
            return packagePrice
        }
        if (item.type === 'pro_subscription') {
            const discount = item.discount || 0
            return applyDiscountToPrice(item.price, discount) * (item.quantity || 1)
        }
        return item.price * (item.quantity || 1)
    }

    function getTotalPrice() {
        const total = cart.reduce((sum, item) => sum + itemKopecks(item), 0)
        return total / 100
    }

    function getSelectedPackage() {
        return cart.find(item => 
            item.type === 'package_v2'
        )
    }

    function getProSubscriptionItem() {
        return cart.find(item => item.type === 'pro_subscription')
    }

    const mainPackage = getSelectedPackage()
    const proItem = getProSubscriptionItem()
    const totalPrice = getTotalPrice()
    const promoBonus = mainPackage
        ? formatPromoPackageBonus(promoCodes, mainPackage.prepaid_documents_count)
        : null

    return (
        <div className="order-info">
            {showTitle && proItem && !mainPackage && (
                <div className="titleContainer">
                    <div className="cart-package-title">{proItem.name}</div>
                </div>
            )}

            {proItem && (() => {
                const discount = proItem.discount || 0
                const originalPrice = (proItem.price || 0) * (proItem.quantity || 1)
                const finalPrice = applyDiscountToPrice(originalPrice, discount)
                return (
                <div className="cart-package-info">
                    <div className="cart-package-details">
                        Подписка Pro
                    </div>
                    <div className="cart-package-details cart-package-details--hint">
                        Брендирование · пакет документов не включён
                    </div>
                    <div className="cart-package-price">
                        {discount > 0 ? (
                            <div className="price-with-discount">
                                <div className="original-price">
                                    {originalPrice / 100} ₽
                                </div>
                                <div className="discounted-price">
                                    {finalPrice / 100} ₽
                                </div>
                                <div className="discount-percent">
                                    -{discount}%
                                </div>
                            </div>
                        ) : (
                            <div>{finalPrice / 100} ₽</div>
                        )}
                    </div>
                    {onRemoveItem && (
                        <button
                            type="button"
                            className="new iconed dark cart-line-remove"
                            style={{ marginTop: 8 }}
                            onClick={() => onRemoveItem(proItem._id, 'pro_subscription')}
                            aria-label="Удалить Pro из корзины"
                        >
                            <img src={trashIcon} alt="" />
                        </button>
                    )}
                </div>
                )
            })()}

            {showTitle && mainPackage && (
                <div className="titleContainer">
                    <div className="cart-package-title">{mainPackage.name}</div>
                </div>
            )}

            {mainPackage && (() => {
                const discount = mainPackage.discount || 0
                const originalPrice = mainPackage.price
                const finalPrice = applyDiscountToPrice(originalPrice, discount)
                
                return (
                    <div className="cart-package-info">
                        <div className="cart-package-details">
                            {mainPackage.prepaid_documents_count} договоров
                            {promoBonus && (
                                <span className="cart-package-bonus" style={{ marginLeft: '6px' }}>
                                    ({promoBonus})
                                </span>
                            )}
                        </div>
                        <div className="cart-package-details">
                            срок {mainPackage.period === 'year' ? '1 год' : '1 месяц'}
                        </div>
                        <div className="cart-package-price">
                            {discount > 0 ? (
                                <div className="price-with-discount">
                                    <div className="original-price">
                                        {originalPrice / 100} ₽
                                    </div>
                                    <div className="discounted-price">
                                        {finalPrice / 100} ₽
                                    </div>
                                    <div className="discount-percent">
                                        -{discount}%
                                    </div>
                                </div>
                            ) : (
                                <div>{finalPrice / 100} ₽</div>
                            )}
                        </div>
                    </div>
                )
            })()}

            {/* Секция проверок подписантов */}
            {mainPackage && mainPackage.tariff_upgrades && mainPackage.tariff_upgrades.length > 0 && (
                <div className="cart-upgrades-section">
                    <div className="cart-section-title">+ проверки подписантов</div>
                    {mainPackage.tariff_upgrades.map((upgradeId: string) => {
                        if (mainPackage.upgrade_additional_price && mainPackage.upgrade_additional_price[upgradeId]) {
                            const upgradePricePerDocument = mainPackage.upgrade_additional_price[upgradeId]
                            const totalUpgradePrice = upgradePricePerDocument / 100
                            const upgradeName = getUpgradeName(upgradeId)
                            
                            return (
                                <div key={upgradeId} className="cart-upgrade-item cart-upgrade-item-row">
                                    <div className="upgrade-text">
                                        <div className="upgrade-name">{upgradeName}</div>
                                        <div className="upgrade-price">{totalUpgradePrice} ₽</div>
                                    </div>
                                    {onRemovePackageUpgrade && (
                                        <button
                                            type="button"
                                            className="new iconed dark cart-line-remove"
                                            onClick={() => onRemovePackageUpgrade(upgradeId)}
                                            aria-label={`Удалить ${upgradeName} из корзины`}
                                        >
                                            <img src={trashIcon} alt="" />
                                        </button>
                                    )}
                                </div>
                            )
                        }
                        return null
                    })}
                </div>
            )}

            {/* Секция отдельных апгрейдов */}
            {cart.filter(item => item.type === 'upgrade').length > 0 && (
                <div className="cart-upgrades-section">
                    <div className="cart-section-title">
                        {mainPackage ? '+ дополнительные проверки' : 'Дополнительные проверки'}
                    </div>
                    {cart.filter(item => item.type === 'upgrade').map((upgrade) => (
                        <div key={upgrade._id} className="cart-upgrade-item cart-upgrade-item-row">
                            <div className="upgrade-text">
                                <div className="upgrade-name">{upgrade.name}</div>
                                <div className="upgrade-price">{(upgrade.price * upgrade.quantity) / 100} ₽</div>
                            </div>
                            {onRemoveItem && (
                                <button
                                    type="button"
                                    className="new iconed dark cart-line-remove"
                                    onClick={() => onRemoveItem(upgrade._id, 'upgrade')}
                                    aria-label={`Удалить ${upgrade.name} из корзины`}
                                >
                                    <img src={trashIcon} alt="" />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Секция дополнительных услуг */}
            {cart.filter(item => isAdditionalService(item.type)).length > 0 && (
                <div className="cart-services-section">
                    <div className="cart-section-title">
                        {mainPackage ? '+ дополнительные услуги' : 'Дополнительные услуги'}
                    </div>
                    {cart.filter(item => isAdditionalService(item.type)).map((service, index) => (
                        <div key={index} className="cart-service-item">
                            <div className="service-name">
                                {service.name}
                            </div>
                            <div className="service-price">{service.price / 100} ₽</div>
                        </div>
                    ))}
                </div>
            )}

            {/* Разделитель после услуг */}
            <div className="cart-divider"></div>

            {/* Итоговый блок */}
            <div className="cart-total-section">
                <div className="titleContainer">
                    <div className="total-label">Итого</div>
                    <div className="total-amount">{totalPrice} ₽</div>
                </div>
                {cart.some(isInstallmentPackage) && (
                    <div className="cart-package-details" data-testid="installment-due-now">
                        Платёж 1 из 3, дальше спишем автоматически
                    </div>
                )}
            </div>
        </div>
    )
}

export default OrderInfo
